import { useState, useCallback } from "react";
import { useFormContext } from "@/context/FormContext";
import VendorService from "@/services/vendorService";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { decodeHtmlEntities } from "@/utils/helper";

export type StorageStatus = "active" | "inactive" | "draft";

const useStorage = (status: StorageStatus = "active") => {
  const { packProductForm, setPackProductForm } = useFormContext();
  const { activeProjectsProductInfo, selectedActiveProject, selectedActiveProjectSelectedVendor } = useGlobalStateContext();
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve product specification id using vendor + variant selection
  const resolveProductSpecificationId = useCallback((variantId?: string | number): string | number | null => {
    try {
      const info = activeProjectsProductInfo;
      if (!info || !Array.isArray(info?.vendorDetails) || info.vendorDetails.length === 0) return null;

      const selectedVendorId = String(selectedActiveProjectSelectedVendor ?? "");
      const vendor = info.vendorDetails.find((v: any) => String(v?.vendorId) === selectedVendorId) || info.vendorDetails[0];
      const specs: any[] = vendor?.vendorIngredientDetails?.productSpecification || [];
      if (!Array.isArray(specs) || specs.length === 0) return null;

      const variants: any[] = Array.isArray((selectedActiveProject as any)?.variants) ? (selectedActiveProject as any).variants : [];
      let index = 0;
      if (variants.length > 0 && (variantId ?? null) !== null && (variantId ?? undefined) !== undefined) {
        const idx = variants.findIndex((v: any) => String(v?.id) === String(variantId));
        index = idx >= 0 ? idx : 0;
      }
      else if((variantId ?? null) !== null) {
        const specIdx = specs.findIndex((spec: any) => String(spec?.id) === String(variantId));
        index = specIdx >= 0 ? specIdx : 0;
      }
      const spec = specs[index] || specs[0];
      return spec?.id ?? null;
    } catch {
      return null;
    }
  }, [activeProjectsProductInfo, selectedActiveProject, selectedActiveProjectSelectedVendor]);
  // Resolve vendor assessment id from selected vendor in Active Projects
  const resolveVendorAssessmentId = useCallback((): string | number | null => {
    try {
      const info = activeProjectsProductInfo;
      if (!info || !Array.isArray(info?.vendorDetails) || info.vendorDetails.length === 0) return null;

      const selectedVendorId = String(selectedActiveProjectSelectedVendor ?? "");
      const vendor = info.vendorDetails.find((v: any) => String(v?.vendorId) === selectedVendorId) || info.vendorDetails[0];
      return vendor?.assessmentId ?? vendor?.id ?? null;
    } catch {
      return null;
    }
  }, [activeProjectsProductInfo, selectedActiveProjectSelectedVendor]);

  const newEntry = {
    primaryShelfLife: "",
    primaryNumber: 0,
    primaryOuterCase: "",
    secondaryShelfLife: "",
    secondaryNumber: 0,
    secondaryOuterCase: "",
    comments: "",
    minimumShelfLifeInfo: "",
    days: 0,
    defineEntireLotCodeInformation: "",
  };

  const newEntryStatement = {
    statement: "",
    useOnPack: "",
  };

  // helpers
  const ensureArray = (v: any): any[] => (Array.isArray(v) ? v : (v ? [v] : []));
  const computeDeletedIds = (
    originalList: any[],
    currentIdList: Array<string | number>
  ): Array<string | number> | undefined => {
    const isValidId = (id: any) => id !== undefined && id !== null && String(id).trim() !== '';
    const existingIds = new Set((originalList || []).map((o: any) => o?.id).filter(isValidId));
    const currentIds = new Set((currentIdList || []).filter(isValidId));
    const deleted = Array.from(existingIds).filter((id: any) => !currentIds.has(id));
    const typed: Array<string | number> = deleted.map((id: any) => (typeof id === 'number' || typeof id === 'string') ? id : String(id));
    return typed.length ? typed : undefined;
  };
  

  const loadStorageData = useCallback(async (activeProjectId: string, variantId?: string | number, subTab?: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const parsedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : (variantId ?? 0);
      // const productSpecId = resolveProductSpecificationId(parsedVariantId); no need this as we are passing the variantId directly
      const productSpecId = parsedVariantId
      if (productSpecId === null || productSpecId === undefined) {
        throw new Error('Missing product specification id');
      }
      
      const vendorAssessmentId = resolveVendorAssessmentId();
      const response = await VendorService.getVendorProductSpecification(String(productSpecId), vendorAssessmentId ?? undefined);
      
      // Decode HTML entities in response data
      const decodedResponse = decodeHtmlEntities(response);
      
      // Preserve raw response (decoded)
      setData(decodedResponse || []);
      
      // Update form with response data if available
      if (decodedResponse && typeof decodedResponse === 'object') {
        // Handle both single object and array responses
        const flattenedData = Array.isArray(decodedResponse)
          ? decodedResponse[0] || {}
          : decodedResponse;

        // Prefer mapping from vendor product specification response shape; fallback to previous structure
        setPackProductForm((prev: any) => {
          const isVendorShape = typeof flattenedData?.id !== 'undefined' && (flattenedData?.packCoding || flattenedData?.codingStatement);
          const packCodingFromApi: any[] = isVendorShape
            ? (Array.isArray(flattenedData?.packCoding) ? flattenedData.packCoding : [])
            : (Array.isArray(flattenedData?.packCoding) ? flattenedData.packCoding : []);
          const mappedPackCodingEntries = packCodingFromApi.length > 0
            ? packCodingFromApi.map((entry: any, index: number) => ({
                id: entry?.id ?? prev?.packCodingEntries?.[index]?.id,
                primaryShelfLife: entry?.primaryShelfLife ?? prev?.packCodingEntries?.[index]?.primaryShelfLife ?? "",
                primaryNumber: entry?.primaryShelfNumber ?? entry?.primaryNumber ?? prev?.packCodingEntries?.[index]?.primaryNumber ?? null,
                primaryOuterCase: entry?.primaryOuterCase === true ? "Yes" : entry?.primaryOuterCase === false ? "No" : (prev?.packCodingEntries?.[index]?.primaryOuterCase ?? ""),
                secondaryShelfLife: entry?.secondaryShelfLife ?? prev?.packCodingEntries?.[index]?.secondaryShelfLife ?? "",
                secondaryNumber: entry?.secondaryShelfNumber ?? entry?.secondaryNumber ?? prev?.packCodingEntries?.[index]?.secondaryNumber ?? null,
                secondaryOuterCase: entry?.secondaryOuterCase === true ? "Yes" : entry?.secondaryOuterCase === false ? "No" : (prev?.packCodingEntries?.[index]?.secondaryOuterCase ?? ""),
                comments: entry?.comments ?? prev?.packCodingEntries?.[index]?.comments ?? "",
                minimumShelfLifeInfo: entry?.minimumShelfLifeInto ?? entry?.minimumShelfLifeInfo ?? prev?.packCodingEntries?.[index]?.minimumShelfLifeInfo ?? "",
                days: entry?.periodValue ?? entry?.days ?? prev?.packCodingEntries?.[index]?.days ?? null,
                defineEntireLotCodeInformation: entry?.defineEntireLotCodeInformation ?? prev?.packCodingEntries?.[index]?.defineEntireLotCodeInformation ?? "",
              }))
            : [newEntry];

          const codingStatementsFromApi: any[] = isVendorShape
            ? (Array.isArray(flattenedData?.codingStatement)
                ? flattenedData.codingStatement
                : (flattenedData?.codingStatement ? [flattenedData.codingStatement] : []))
            : (Array.isArray(flattenedData?.shelfLifeCodingStatement)
                ? flattenedData.shelfLifeCodingStatement
                : []);

          const mappedShelfLifeCodingStatementEntries = codingStatementsFromApi.length > 0
            ? codingStatementsFromApi.map((entry: any, index: number) => ({
                id: entry?.id ?? prev?.shelfLifeCodingStatementEntries?.[index]?.id,
                statement: entry?.statement ?? prev?.shelfLifeCodingStatementEntries?.[index]?.statement ?? "",
                useOnPack: entry?.useOnPack ?? prev?.shelfLifeCodingStatementEntries?.[index]?.useOnPack ?? "",
              }))
            : [newEntryStatement];
          const attachmentsFromApi: any[] = isVendorShape
            ? (Array.isArray(flattenedData?.storageDocuments)
                ? flattenedData.storageDocuments.map((d: any) => ({
                    id: String(d?.document?.id ?? ""),
                    fileName: String(d?.documentTitle ?? ""),
                    type: "document",
                    uploadDate: new Date(),
                  }))
                : (prev?.attachments || []))
            : (Array.isArray(flattenedData?.attachments) ? flattenedData.attachments : prev?.attachments || []);

          const nextForm = {
            ...prev,
            // Storage Details (flattened in form)
            primaryShelfLifeCodingFromDateOf: (isVendorShape ? (flattenedData?.primaryShelfLifeCodingFromDateOf ?? "") : (flattenedData?.storageDetails?.primaryShelfLifeCodingFromDateOf ?? "")) ||  "",
            period: (isVendorShape ? (flattenedData?.period ?? "") : (flattenedData?.storageDetails?.period ?? "")) || "",
            formatOfShelfLifeCoding: (isVendorShape ? (flattenedData?.formatOfShelfLifeCoding ?? "") : (flattenedData?.storageDetails?.formatOfShelfLifeCoding ?? "")) || "",
            bestByDateImprintRequired: (isVendorShape ? ((flattenedData?.bestByDateImprintAreaRequiredonDieline === 1) ? "YES" : (flattenedData?.bestByDateImprintAreaRequiredonDieline === 0 ? "NO" : "")) : (flattenedData?.storageDetails?.bestByDateImprintRequired ?? "")) || "",
            // Sections
            packCodingEntries: mappedPackCodingEntries,
            shelfLifeCodingStatementEntries: mappedShelfLifeCodingStatementEntries,
            attachments: attachmentsFromApi,
          };

          return nextForm;
        });

        // Form updated
      }
      
      return response;
      
    } catch (error: any) {
      setError(error.message || "Failed to load storage data");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [resolveProductSpecificationId, resolveVendorAssessmentId, setPackProductForm]);

  const saveStorageData = useCallback(async (payload: any, activeProjectId: string, variantId?: string | number, subTab?: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Build payload for vendor product specification PUT
      // const productSpecId = resolveProductSpecificationId(variantId); no need this as we are passing the variantId directly
      const productSpecId = variantId;
      const toBool = (v: any): boolean | null => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') {
          const lower = v.toLowerCase().trim();
          if (lower === 'yes' || lower === 'true') return true;
          if (lower === 'no' || lower === 'false') return false;
          return null;  // Empty string → null (not selected)
        }
        if (typeof v === 'number') return v !== 0;
        return null;  // undefined/null → null (not selected)
      };
      const yesNoToInt = (v: any) => {
        if (v === null || v === undefined) return undefined;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
          const raw = v.trim();
          if (raw === '') return undefined;
          const s = raw.toLowerCase();
          if (s === 'yes') return 1;
          if (s === 'no') return 0;
          const n = Number(raw);
          if (!Number.isNaN(n)) return n;
        }
        return undefined;
      };

      const packCodingArray: any[] = Array.isArray(packProductForm?.packCodingEntries)
        ? packProductForm.packCodingEntries.map((pc: any, index: number) => ({
            id: pc?.id ?? undefined,
            primaryShelfLife: pc?.primaryShelfLife ?? '',
            primaryShelfNumber: pc?.primaryNumber == null || pc.primaryNumber === "" ? null : Number(pc.primaryNumber),
            primaryOuterCase: toBool(pc?.primaryOuterCase),
            secondaryShelfLife: pc?.secondaryShelfLife ?? '',
            secondaryShelfNumber: pc?.secondaryNumber == null || pc.secondaryNumber === "" ? null : Number(pc.secondaryNumber),
            secondaryOuterCase: toBool(pc?.secondaryOuterCase),
            comments: pc?.comments ?? '',
            minimumShelfLifeInto: pc?.minimumShelfLifeInfo ?? '',
            periodValue: pc?.days == null || pc.days === "" ? null : Number(pc.days),
            defineEntireLotCodeInformation: pc?.defineEntireLotCodeInformation ?? ''
          }))
        : [];
      const storageDocuments = ((): any[] => {
        // Prefer payload.storageDocuments if provided
        const provided = Array.isArray((payload as any)?.storageDocuments) ? (payload as any).storageDocuments : undefined;
        if (provided && provided.length > 0) return provided;
        // Fallback to form attachments mapping
        if (Array.isArray(packProductForm?.attachments)) {
          return packProductForm.attachments.map((att: any, idx: number) => ({
            index: idx,
            documentTitle: String(att?.fileName || att?.documentTitle || att?.name || '').trim(),
            document: {
              id: ((): any => {
                const raw = att?.id ?? att?.documentId;
                const n = Number(raw);
                return Number.isNaN(n) ? raw : n;
              })()
            }
          }));
        }
        return [];
      })();


      const vendorPayload: any = {
        id: ((): any => {
          const n = Number(productSpecId);
          return Number.isNaN(n) ? productSpecId : n;
        })(),
        primaryShelfLifeCodingFromDateOf: packProductForm?.primaryShelfLifeCodingFromDateOf ?? '',
        period: packProductForm?.period ?? '',
        formatOfShelfLifeCoding: packProductForm?.formatOfShelfLifeCoding ?? '',
        bestByDateImprintAreaRequiredonDieline: yesNoToInt(packProductForm?.bestByDateImprintRequired),
        // Include validation from caller payload (API expects string)
        isStorageValidated: (payload as any)?.isStorageValidated !== undefined ? String((payload as any).isStorageValidated) : undefined,
        codingStatements: ((): any => {
          const arr = Array.isArray(packProductForm?.shelfLifeCodingStatementEntries)
            ? packProductForm.shelfLifeCodingStatementEntries
            : [];
          const mapped = arr
            .map((entry: any) => {
              const statement = entry?.statement;
              const useOnPack = entry?.useOnPack;
              const id = entry?.id;
              if (!statement && !useOnPack && !id) return null;
              const payload: any = { statement: statement ?? '', useOnPack: useOnPack ?? '' };
              if (id !== undefined && id !== null && String(id).trim() !== '') {
                payload.id = id;
              }
              return payload;
            })
            .filter(Boolean);
          return mapped.length > 0 ? mapped : undefined;
        })(),
        storageDocuments,
        packCodings: packCodingArray.length > 0 ? packCodingArray : undefined,
        // Deletions (compare previously-loaded data vs current form state)
        packCodingsToDelete: computeDeletedIds(
          ensureArray((data as any)?.packCodings || (data as any)?.packCoding),
          (Array.isArray(packProductForm?.packCodingEntries)
            ? packProductForm.packCodingEntries.map((pc: any) => pc?.id)
            : []) as Array<string | number>
        ),
        codingStatementsToDelete: computeDeletedIds(
          ensureArray((data as any)?.codingStatements || (data as any)?.codingStatement),
          (Array.isArray(packProductForm?.shelfLifeCodingStatementEntries)
            ? packProductForm.shelfLifeCodingStatementEntries.map((cs: any) => cs?.id)
            : []) as Array<string | number>
        ),
      };
      // Attach transitionRequest if provided by caller
      if (payload && (payload as any).transitionObject) {
        vendorPayload.transitionObject = (payload as any).transitionObject;
      }

      // Remove undefined keys to keep payload clean
      Object.keys(vendorPayload).forEach((k) => vendorPayload[k] === undefined && delete vendorPayload[k]);
      const response = await VendorService.updateVendorProductSpecification(String(productSpecId), vendorPayload);
      setData(response?.data || response || []);
    } catch (error: any) {
      setError(error.message || "Failed to save storage data");
      setData([]);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [packProductForm, resolveProductSpecificationId]);

  // Filter data based on status if needed in the future
  const filteredData = data;

  return {
    loadStorageData,
    saveStorageData,
    data: filteredData,
    loading: isLoading,
    error,
    reload: async () => {
      setData([]);
      setError(null);
    },
    ensureArray,
    computeDeletedIds,
  };
};

export default useStorage;
